/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import getWorkflowRunStatusPresentation from '@/lib/getWorkflowRunStatusPresentation.js';
import { isWorkflowRunCancelable, isWorkflowRunTerminal } from '@/lib/workflows/workflowRunStatus.js';
import addStepAppMetadata from '@/lib/workflows/addStepAppMetadata.js';
import resolveWorkflowRunChangesState from '@/lib/workflows/resolveWorkflowRunChangesState.js';
import resolveWorkflowRunEnvironmentName from '@/lib/workflows/resolveWorkflowRunEnvironmentName.js';
import formatWorkflowRunVersionLabel from '@/lib/workflows/formatWorkflowRunVersionLabel.js';
import { appsMetadataItems, companyEnvironments, workflowRunOperationsList, workflowRunTimeMachineList, workflows } from '@/redux/selectors/pieveSelectors/baseSelectors.js';
import { activeThreadDraftWorkflow } from '@/redux/selectors/pieveSelectors/composerResolutionSelectors.js';
import buildAppMetadataMaps from '@/redux/selectors/pieveSelectors/lib/buildAppMetadataMaps.js';
import buildWorkflowRunChanges from '@/redux/selectors/pieveSelectors/lib/buildWorkflowRunChanges.js';
import buildWorkflowRunFailure from '@/redux/selectors/pieveSelectors/lib/buildWorkflowRunFailure.js';
import buildWorkflowRunFailureOrigins from '@/redux/selectors/pieveSelectors/lib/buildWorkflowRunFailureOrigins.js';
import buildWorkflowRunFlowControl from '@/redux/selectors/pieveSelectors/lib/buildWorkflowRunFlowControl.js';
import buildWorkflowRunStepStatusByStepId from '@/redux/selectors/pieveSelectors/lib/buildWorkflowRunStepStatusByStepId.js';
import buildWorkflowRunFacts from '@/redux/selectors/pieveSelectors/lib/buildWorkflowRunFacts.js';
import buildWorkflowStepDisplayNameMap from '@/redux/selectors/pieveSelectors/lib/buildWorkflowStepDisplayNameMap.js';
import flattenWorkflowRunTimeMachineRows from '@/redux/selectors/pieveSelectors/lib/flattenWorkflowRunTimeMachineRows.js';
import { automationWorkflowRunLogsModal, workflowTestSavedById, workflowTestSetup } from '@/redux/selectors/uiSelectors.js';
import buildWorkflowTestRequestSteps from '@/lib/workflows/buildWorkflowTestRequestSteps.js';
import resolveWorkflowTestChoices from '@/lib/workflows/resolveWorkflowTestChoices.js';
import newerWorkflowTest from '@/lib/workflows/newerWorkflowTest.js';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

export const workflowRunTimeMachineEntry = (state, workflowRunId) => {
  const id = String(workflowRunId || '').trim();

  if (!id) {
    return null;
  }

  return workflowRunTimeMachineList(state).find((entry) => String(entry?.workflowRunId || '').trim() === id) || null;
};

export const workflowRunTimeMachineRows = createSelector([workflowRunTimeMachineEntry], (entry) => {
  const nodes = entry?.payload?.timeMachine;

  if (!Array.isArray(nodes) || nodes.length < 1) {
    return EMPTY_ARRAY;
  }

  return flattenWorkflowRunTimeMachineRows(nodes);
});

/* App icon and name are not part of a workflow definition, so the live workflow record is still consulted for its
   `appMetadataById` map. Step structure and step names must not come from here: it is whatever this client last
   hydrated, which is why two clients rendered the same run differently once a step was renamed or deleted. */
export const workflowRunMatchedWorkflow = createSelector([workflowRunTimeMachineEntry, workflows], (entry, allWorkflows) => {
  const workflowId = String(entry?.run?.workflowId || entry?.payload?.run?.workflowId || '').trim();

  if (!workflowId) {
    return null;
  }

  return allWorkflows.find((item) => String(item?.workflowId || '').trim() === workflowId) || null;
});

export const workflowRunDefinitionSteps = createSelector([workflowRunTimeMachineEntry, workflowRunMatchedWorkflow, appsMetadataItems], (entry, matchedWorkflow, appsMeta) => {
  // The whole definition, source step included: the tree is the run read against the workflow as built, so a
  // reader sees every step and which ones this execution reached. The source step never executes and the row
  // renders it without a status.
  const definitionSteps = entry?.workflow?.definition?.steps;
  const steps = Array.isArray(definitionSteps) ? definitionSteps : EMPTY_ARRAY;

  if (steps.length < 1) {
    return EMPTY_ARRAY;
  }

  const { metadataById, metadataBySlug } = buildAppMetadataMaps({
    appsMetadataItems: appsMeta,
    appMetadataById: matchedWorkflow?.appMetadataById
  });

  return addStepAppMetadata(steps, metadataById, metadataBySlug);
});

export const workflowRunFailureOrigins = createSelector([workflowRunTimeMachineRows], (rows) => {
  return buildWorkflowRunFailureOrigins(rows);
});

export const workflowRunStepStatusByStepId = createSelector([workflowRunTimeMachineRows, workflowRunFailureOrigins], (rows, failureOriginIndexes) => {
  return buildWorkflowRunStepStatusByStepId({ rows, failureOriginIndexes });
});

export const workflowRunStepDisplayNameByStepId = createSelector([workflowRunDefinitionSteps], (steps) => {
  if (steps.length < 1) {
    return EMPTY_OBJECT;
  }

  return buildWorkflowStepDisplayNameMap(steps);
});

export const workflowRunFacts = createSelector([workflowRunTimeMachineEntry, workflowRunTimeMachineRows], (entry, rows) => {
  return buildWorkflowRunFacts({
    run: entry?.run,
    truncatedPayloadCount: rows.filter((row) => row.truncation.request || row.truncation.response || row.truncation.error).length
  });
});

// The workflow's name for the modal title, from the workflow the read service resolved for this run (its snapshot, else the current draft).
export const workflowRunWorkflowName = (state, workflowRunId) => String(workflowRunTimeMachineEntry(state, workflowRunId)?.workflow?.name || '').trim();

// Null for a default-environment run, which is nearly all of them, so the modal renders no tag at all.
export const workflowRunEnvironmentName = createSelector([workflowRunTimeMachineEntry, companyEnvironments], (entry, environments) => {
  return resolveWorkflowRunEnvironmentName({ run: entry?.run, environments });
});

export const workflowRunVersionLabel = (state, workflowRunId) => formatWorkflowRunVersionLabel(workflowRunTimeMachineEntry(state, workflowRunId)?.run);

export const workflowRunStatusPresentation = createSelector([workflowRunTimeMachineEntry], (entry) => {
  return getWorkflowRunStatusPresentation(entry?.payload?.status || entry?.run?.status, entry?.payload?.failureReason || entry?.run?.failureReason, entry?.run?.meta?.workflowRunSummary);
});

// The Test window tests the draft of the thread it was opened from; its tree and names come from that draft.
export const workflowTestDraftSteps = createSelector([activeThreadDraftWorkflow], (workflow) => {
  const steps = workflow?.definition?.steps;

  return Array.isArray(steps) ? steps : EMPTY_ARRAY;
});

export const workflowTestDraftStepNameByStepId = createSelector([workflowTestDraftSteps], (steps) => {
  return steps.length > 0 ? buildWorkflowStepDisplayNameMap(steps) : EMPTY_OBJECT;
});

// The open Test window's setup (operations, loop candidates, schemas), for the workflow it tests.
export const workflowTestWindowSetup = (state) => workflowTestSetup(state, automationWorkflowRunLogsModal(state)?.workflowId);

// A workflow's stored test (draft meta.test, with the revision Run test sends): the later by revision of what this
// window last heard (its own save, /test-setup, Pieve's push) and the copy on the draft the page loaded, so the
// window opens with it before /test-setup lands.
export const workflowStoredTest = createSelector([workflowTestSavedById, activeThreadDraftWorkflow, (_state, workflowId) => String(workflowId || '').trim()], (savedById, draft, workflowId) => {
  return newerWorkflowTest(draft?.workflowId === workflowId ? draft?.meta?.test : null, savedById?.[workflowId]);
});

export const workflowTestWindowSaved = (state) => workflowStoredTest(state, automationWorkflowRunLogsModal(state)?.workflowId);

// The Test window's choices: the person's unsaved edits, or else the stored test read against the draft as it is
// now. A past choice that no longer fits stays, as { dropped }.
export const workflowTestChoices = createSelector([automationWorkflowRunLogsModal, workflowTestWindowSaved, workflowTestDraftSteps, workflowTestWindowSetup], (modal, saved, steps, setup) => {
  return modal?.testChoices ?? resolveWorkflowTestChoices({ steps: saved?.steps, definitionSteps: steps, loops: setup?.loops || EMPTY_ARRAY });
});

// Those choices as the `test.steps` Run test sends (and the one Pieve reads when the person writes from the
// window), with the JSON that doesn't parse and the past choices that no longer fit, each of which holds Run test.
export const workflowTestRequest = createSelector([workflowTestChoices, workflowTestDraftSteps], (choices, steps) => {
  return buildWorkflowTestRequestSteps({ choices, steps });
});

// A person's test from the Test window carries its choices; Test again reopens the window with them.
export const workflowRunTestSteps = createSelector([workflowRunTimeMachineEntry], (entry) => {
  const steps = entry?.run?.meta?.test?.steps;

  return steps && typeof steps === 'object' && !Array.isArray(steps) ? steps : null;
});

// A finished test's receipt (C2, run meta.testReceipt): what it wrote, what failed, what it never ran.
export const workflowRunTestReceipt = (state, workflowRunId) => workflowRunTimeMachineEntry(state, workflowRunId)?.run?.meta?.testReceipt || null;

export const workflowRunIsCancelable = createSelector([workflowRunTimeMachineEntry], (entry) => {
  return isWorkflowRunCancelable({ status: entry?.payload?.status || entry?.run?.status });
});

// A webhook call can be sent again once its run has ended, when the run kept the call's payload. Waiting for the end
// keeps a resend from racing the run it repeats.
export const workflowRunCanResendWebhook = createSelector([workflowRunTimeMachineEntry], (entry) => {
  const run = entry?.run;
  const triggerStepId = run?.meta?.triggerStepId;

  return run?.sourceType === 'webhook' && Boolean(triggerStepId && run.meta?.runtime?.sourceStepOutputs?.[triggerStepId]) && isWorkflowRunTerminal({ status: entry?.payload?.status || run.status });
});

// A run stopped before it executed (task limit) has no time machine artifact, so its failure lives only on the run row.
export const workflowRunFailure = createSelector([workflowRunTimeMachineEntry, workflowRunTimeMachineRows, workflowRunFailureOrigins, workflowRunStepDisplayNameByStepId], (entry, rows, failureOriginIndexes, stepNameByStepId) => {
  return buildWorkflowRunFailure({
    failurePayload: entry?.payload?.failure || entry?.run?.meta?.workflowRunFailure,
    rows,
    failureOriginIndexes,
    stepNameByStepId
  });
});

export const workflowRunRawPayload = createSelector([workflowRunTimeMachineEntry], (entry) => {
  if (!entry) {
    return null;
  }

  return {
    run: entry.run || null,
    payload: entry.payload || null,
    artifact: entry.artifact || null,
    warning: entry.warning || null
  };
});

export const workflowRunFlowControl = createSelector([workflowRunTimeMachineEntry, workflowRunStepDisplayNameByStepId], (entry, stepNameByStepId) => {
  return buildWorkflowRunFlowControl({
    meta: entry?.run?.meta,
    stepNameByStepId
  });
});

export const workflowRunTimeMachineWarning = (state, workflowRunId) => {
  return workflowRunTimeMachineEntry(state, workflowRunId)?.warning || null;
};

// The run record has arrived; a queued or running run has it before any artifact exists.
export const workflowRunIsLoaded = (state, workflowRunId) => Boolean(workflowRunTimeMachineEntry(state, workflowRunId)?.run);

export const workflowRunTimeMachineHasPayload = (state, workflowRunId) => {
  return Boolean(workflowRunTimeMachineEntry(state, workflowRunId)?.payload);
};

export const workflowRunOperations = createSelector([workflowRunOperationsList, (_state, workflowRunId) => workflowRunId], (operations, workflowRunId) => {
  return operations.filter((operation) => operation.runId === workflowRunId);
});

// The changes the run made, grouped for its logs' Changes section.
// The changes the run made, grouped for its logs' Changes view, each tied to the time machine row of the call that made it.
export const workflowRunChanges = createSelector([workflowRunOperations, appsMetadataItems, workflowRunTimeMachineRows], (operations, appsMeta, rows) => {
  return buildWorkflowRunChanges({ operations, appsMetadataItems: appsMeta, rows });
});

/* Whether the run's changes have all landed in the ledger yet. Not memoized: the answer moves with the clock, and the
   modal re-renders on every refetch while it is still `recording`. */
export const workflowRunChangesState = (state, workflowRunId) => {
  return resolveWorkflowRunChangesState({ run: workflowRunTimeMachineEntry(state, workflowRunId)?.run, recordedCount: workflowRunOperations(state, workflowRunId).length });
};
